import React, { useState, useRef, useEffect } from 'react';

import {
  RequestAQuoteFormProps,
  ModalProps
} from '../../components/request-a-quote-form/types';
import { ESCAPE } from '../../../helpers/keycodes';
import RequestAQuoteForm from '../request-a-quote-form';
import { ExtendedWindowType } from '../../engine';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import {
  disableBodyScroll,
  clearAllBodyScrollLocks,
  BodyScrollOptions
} from 'body-scroll-lock';

const ContractorRequestAForm: React.FC<RequestAQuoteFormProps> = ({
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [contractorData, setContractorData] = useState<ModalProps | null>(null);
  const [listener, setListener] = useState(false);
  const [focusTrap, setFocusTrap] = useState<FocusTrap | null>(null);
  const modal = useRef(null);

  const handleOpenModal = (_contractorData: ModalProps) => {
    setContractorData(_contractorData);
    setOpen(true);
    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event: 'modal_interaction',
      event_category: 'modals',
      event_action: 'view',
      event_attributes: {
        contractor_id: _contractorData?.id,
        contractor_name: _contractorData?.contractorName,
        contractor_certificates_count: _contractorData?.numberOfCertificates,
        contractor_rating: _contractorData?.rating,
        contractor_reviews_count: _contractorData?.numberOfReviews,
        contractor_certificate_name: _contractorData?.certificateName,
        contractor_type: _contractorData?.type
      }
    });

    global.dataLayer?.push({
      event: 'request_quote_form_view',
      event_category: 'find a contractor',
      event_action: 'request a quote form view',
      event_attributes: {
        contractor_id: _contractorData?.id,
        contractor_name: _contractorData?.contractorName,
        contractor_certificates_count: _contractorData?.numberOfCertificates,
        contractor_rating: _contractorData?.rating,
        contractor_reviews_count: _contractorData?.numberOfReviews,
        contractor_certificate_name: _contractorData?.certificateName,
        contractor_type: _contractorData?.type,
        form_name: 'request a quote form'
      }
    });
  };

  useEffect(() => {
    if (modal.current) {
      const options = {
        reserveScrollBarGap: true,
        allowTouchMove: (el: HTMLElement) => {
          return el.closest('.modal__content') !== null;
        }
      } as BodyScrollOptions;

      const trap = createFocusTrap(modal.current, {
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: true,
        onActivate() {
          disableBodyScroll(document.body, options);
          document.body.style.overflow = 'hidden';
        },
        onDeactivate() {
          clearAllBodyScrollLocks();
          document.body.style.removeProperty('overflow');
        }
      });

      setFocusTrap(trap);

      (modal.current as HTMLDialogElement).addEventListener('close', () => {
        setOpen(false);
        const global = window as ExtendedWindowType;

        global.dataLayer?.push({
          event: 'modal_interaction',
          event_category: 'modals',
          event_action: 'close',
          event_attributes: {
            contractor_id: contractorData?.id,
            contractor_name: contractorData?.contractorName,
            contractor_certificates_count: contractorData?.numberOfCertificates,
            contractor_rating: contractorData?.rating,
            contractor_reviews_count: contractorData?.numberOfReviews,
            contractor_certificate_name: contractorData?.certificateName,
            contractor_type: contractorData?.type
          }
        });
      });
    }

    window.addEventListener('keydown', handleEsc);

    if (!listener) {
      window.addEventListener('openRequestAQuoteModal', e =>
        handleOpenModal((e as CustomEvent).detail as ModalProps)
      );
      setListener(true);
    }
    return () => {
      if (modal.current) {
        (modal.current as HTMLDialogElement).removeEventListener('close', () =>
          setOpen(false)
        );
      }

      window.removeEventListener('keydown', handleEsc);

      if (listener) {
        window.removeEventListener('openRequestAQuoteModal', e =>
          handleOpenModal((e as CustomEvent).detail as ModalProps)
        );
        setListener(false);
      }
    };
  }, [modal.current]);

  useEffect(() => {
    if (modal.current) {
      if (open) {
        (modal.current as HTMLDialogElement).show();
        focusTrap?.activate();
      } else {
        setContractorData(null);
        focusTrap?.deactivate();
      }
    }
  }, [open]);

  const handleClose = (
    e: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    if (
      (e.target as Element).classList.contains('modal__backdrop') ||
      (e.target as Element).classList.contains('modal__close-button')
    ) {
      if (modal.current) {
        (window as ExtendedWindowType).dataLayer?.push({
          event: 'request_quote_abandon',
          event_category: 'find a contractor',
          event_action: 'form abandoned',
          event_attributes: {
            contractor_id: contractorData?.id,
            contractor_name: contractorData?.contractorName,
            contractor_certificates_count: contractorData?.numberOfCertificates,
            contractor_rating: contractorData?.rating,
            contractor_reviews_count: contractorData?.numberOfReviews,
            contractor_certificate_name: contractorData?.certificateName,
            contractor_type: contractorData?.type,
            form_name: 'request a quote',
            form_type: 'contractor locator'
          }
        });
        setOpen(false);
        setContractorData(null);
        (modal.current as HTMLDialogElement).close();
      }
    }
  };

  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === ESCAPE) {
      if (modal.current) {
        setOpen(false);
        (modal.current as HTMLDialogElement).close();
      }
    }
  };

  return (
    <div
      className="modal__contractor"
      style={!open ? { display: 'none' } : undefined}
    >
      <div
        className="modal__backdrop"
        role="presentation"
        onClick={handleClose}
      >
        {' '}
      </div>
      <dialog ref={modal} className="modal">
        <div className="modal__content">
          <button
            className="modal__close-button"
            aria-label="close"
            title="close"
            onClick={handleClose}
          ></button>
          {contractorData && (
            <RequestAQuoteForm
              {...props}
              modal={contractorData as ModalProps}
            ></RequestAQuoteForm>
          )}
        </div>
      </dialog>
    </div>
  );
};

export default ContractorRequestAForm;
